import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { getFeaturedMovies, getTrendingMovies } from '../../utils/database';
import './Hero.css';

const Hero = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [heroMovies, setHeroMovies] = useState([]);
  const [isPaused, setIsPaused] = useState(false);


  useEffect(() => {
    // Get featured or trending movies for the hero carousel
    const featured = getFeaturedMovies();
    const trending = getTrendingMovies();
    // const movies = featured.length > 0 ? featured : trending.slice(0, 3);
    const movies = featured.length > 1 ? featured : trending;
    setHeroMovies(movies);
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (heroMovies.length <= 1 || isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % heroMovies.length);
    }, 3000); // Change slide every 3 seconds

    return () => clearInterval(interval);
  }, [heroMovies.length, isPaused]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % heroMovies.length);
    setIsPaused(true);
    // Resume auto-play after 5 seconds of manual interaction
    setTimeout(() => setIsPaused(false), 5000);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + heroMovies.length) % heroMovies.length);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
  };

  if (heroMovies.length === 0) {
    return null;
  }

  const currentMovie = heroMovies[currentIndex];

  return (
    <section 
      className="hero"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="hero__background">
        <AnimatePresence mode="wait">
          <motion.img 
            key={currentMovie.id}
            src={currentMovie.backdrop || currentMovie.poster} 
            alt={currentMovie.title}
            className="hero__background-image"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7 }}
          />
        </AnimatePresence>
        <div className="hero__gradient hero__gradient--horizontal"></div>
        <div className="hero__gradient hero__gradient--vertical"></div>
      </div>

      <div className="hero__content">
        <AnimatePresence mode="wait">
          <motion.div 
            className="hero__info"
            key={currentMovie.id}
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 30 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="hero__title">
              {currentMovie.title}
            </h2>

            <div className="hero__meta">
              <div className="hero__rating">
                <span>IMDB</span>
                <span>{currentMovie.rating}</span>
              </div>
              <span className="hero__meta-item">({currentMovie.votes})</span>
              <span className="hero__meta-dot"></span>
              <span className="hero__meta-item">{currentMovie.year}</span>
              <span className="hero__meta-dot"></span>
              <span className="hero__meta-item">{currentMovie.duration}</span>
              <span className="hero__meta-dot"></span>
              <span className="hero__meta-item">{currentMovie.genre}</span>
            </div>

            <p className="hero__description">
              {currentMovie.description.substring(0, 200)}... 
              <a 
                onClick={() => navigate(`/movie/${currentMovie.id}`)} 
                className="hero__see-more"
              >
                See more
              </a>
            </p>

            <div className="hero__actions">
              <button 
                className="hero__button hero__button--outline"
                onClick={() => window.open(currentMovie.trailerUrl, '_blank')}
              >
                <PlayArrowIcon />
                <span>Watch trailer</span>
              </button>
              <button 
                className="hero__button hero__button--primary"
                onClick={() => navigate(`/movie/${currentMovie.id}`)}
              >
                <PlayArrowIcon />
                <span>Watch now</span>
              </button>
            </div>
          </motion.div>
        </AnimatePresence>

        {heroMovies.length > 1 && (
          <>
            <motion.button 
              className="hero__nav hero__nav--prev"
              onClick={prevSlide}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <ArrowBackIosIcon />
            </motion.button>
            
            <motion.button 
              className="hero__nav hero__nav--next"
              onClick={nextSlide}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <ArrowForwardIosIcon />
            </motion.button>
          </>
        )}
        
        {heroMovies.length > 1 && (
          <div className="hero__indicators">
            {heroMovies.map((_, index) => (
              <button
                key={index}
                className={`hero__indicator ${index === currentIndex ? 'hero__indicator--active' : ''}`}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Hero;